import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Loader2, XCircle, Shield, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';

const PaymentLinkPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { document.title = 'Pay | CoinPayUp'; }, []);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const res = await axios.get(`${API}/p/${slug}`);
        setLinkData(res.data);
        if (res.data.default_network_id) {
          setSelectedNetwork(res.data.default_network_id);
        }
      } catch {
        setLinkData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLink();
  }, [slug]);

  const handleProceed = async () => {
    if (!selectedNetwork || !selectedToken) return;
    const wallet = linkData.wallets.find(w => w.network_id === selectedNetwork);
    if (!wallet) { toast.error('No wallet available for this network'); return; }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/p/${slug}/pay`, {
        wallet_id: wallet.id,
        currency: selectedToken,
      });
      navigate(`/checkout/${res.data.invoice_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const availableTokens = linkData?.tokens?.filter(t => t.network_id === selectedNetwork) || [];
  const selectedNetworkObj = linkData?.networks?.find(n => n.id === selectedNetwork);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-testid="paylink-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#6c63ff]" />
      </div>
    );
  }

  if (!linkData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="paylink-not-found">
        <div className="max-w-sm w-full text-center">
          <XCircle className="h-10 w-10 text-white/20 mx-auto mb-6" />
          <h2 className="text-lg font-medium text-white mb-2">Payment link not found</h2>
          <p className="text-white/40 text-sm">This link may be inactive or doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-4 py-12" data-testid="paylink-page">
      <div className="w-full max-w-[460px]">
        <div className="bg-[#141414] border border-white/[0.08] rounded-2xl overflow-hidden">

          {/* Header */}
          <div className="px-7 pt-7 pb-5">
            <p className="text-white/40 text-sm mb-1">Payment Amount</p>
            <p className="text-white text-4xl font-bold tracking-tight" data-testid="paylink-amount">
              {linkData.amount} <span className="text-white/40 text-xl font-medium">{linkData.currency || ''}</span>
            </p>
            {linkData.description && (
              <p className="text-white/30 text-sm mt-2">{linkData.description}</p>
            )}
          </div>

          <div className="h-px bg-white/[0.06]" />

          {/* Step 1: Select Network */}
          {step === 1 && (
            <div className="px-7 py-6" data-testid="paylink-step-network">
              <h3 className="text-white text-lg font-semibold mb-1">Select Network</h3>
              <p className="text-white/40 text-sm mb-5">Choose the blockchain network for your payment</p>
              <div className="space-y-2.5 mb-6">
                {linkData.networks.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { setSelectedNetwork(n.id); setSelectedToken(null); }}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                      selectedNetwork === n.id
                        ? 'border-[#6c63ff] bg-[#6c63ff]/[0.08]'
                        : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'
                    }`}
                    data-testid={`network-option-${n.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        selectedNetwork === n.id ? 'bg-[#6c63ff]/20 text-[#6c63ff]' : 'bg-white/[0.06] text-white/50'
                      }`}>
                        {n.symbol?.slice(0, 3)}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{n.name}</p>
                        <p className="text-white/30 text-xs">{n.symbol}</p>
                      </div>
                    </div>
                    {selectedNetwork === n.id && <Check className="h-5 w-5 text-[#6c63ff]" />}
                  </button>
                ))}
              </div>
              <Button
                className="w-full h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all disabled:opacity-40"
                disabled={!selectedNetwork}
                onClick={() => setStep(2)}
                data-testid="paylink-next-network"
              >
                Continue <ChevronRight className="h-5 w-5 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 2: Select Token */}
          {step === 2 && (
            <div className="px-7 py-6" data-testid="paylink-step-token">
              <h3 className="text-white text-lg font-semibold mb-1">Select Token</h3>
              <p className="text-white/40 text-sm mb-5">
                Choose the token on <span className="text-white/60 font-medium">{selectedNetworkObj?.name}</span>
              </p>
              <div className="space-y-2.5 mb-6">
                {availableTokens.map((t) => (
                  <button
                    key={t.id || t.symbol}
                    onClick={() => setSelectedToken(t.symbol)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                      selectedToken === t.symbol
                        ? 'border-[#6c63ff] bg-[#6c63ff]/[0.08]'
                        : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'
                    }`}
                    data-testid={`token-option-${t.symbol}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        selectedToken === t.symbol ? 'bg-[#6c63ff]/20 text-[#6c63ff]' : 'bg-white/[0.06] text-white/50'
                      }`}>
                        {t.symbol?.slice(0, 4)}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{t.name}</p>
                        <p className="text-white/30 text-xs">{t.symbol}</p>
                      </div>
                    </div>
                    {selectedToken === t.symbol && <Check className="h-5 w-5 text-[#6c63ff]" />}
                  </button>
                ))}
                {availableTokens.length === 0 && (
                  <p className="text-white/30 text-sm text-center py-4">No tokens available for this network</p>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  className="flex-1 h-14 bg-white/[0.1] hover:bg-white/[0.15] text-white font-semibold rounded-xl text-lg transition-all"
                  onClick={() => setStep(1)}
                  data-testid="paylink-back-token"
                >
                  Back
                </Button>
                <Button
                  className="flex-1 h-14 bg-[#6c63ff] hover:bg-[#5b54e6] text-white font-semibold rounded-xl text-lg tracking-wide transition-all disabled:opacity-40"
                  disabled={!selectedToken || creating}
                  onClick={handleProceed}
                  data-testid="paylink-proceed"
                >
                  {creating ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                  {creating ? 'Creating...' : 'Proceed to Pay'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-1.5 mt-8">
          <Shield className="h-3 w-3 text-white/15" />
          <span className="text-white/15 text-xs tracking-wide">Powered by CoinPayUp</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinkPage;
